import { JSX, useEffect, useState } from 'react';

// Hooks and services
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { getPairingInfoByDeviceId, getPairingInfoByEquipmentId } from '../../../services/pairing.service';
import { pairDevice, unpairDevice } from '../../../services/device.service';

// Types
import { PairingRequest } from '../../../requestModels/pairingRequest';
import { UnpairRequest } from '../../../requestModels/unpairRequest';

// Components
import { PairingInfo } from '../../../types/PairingInfo';
import PairingInfoDetails from './PairingInfoDetails';

interface PairingProps {
  deviceId?: string;
  equipmentId?: string;
  noHistoryCardRadius?: boolean;
}

export default function Pairing(props: PairingProps): JSX.Element {
  const [historyNeedsUpdate, setHistoryNeedsUpdate] = useState(false);
  const { notifyMessage } = useNotifyMessage();
  const [pairingInfo, setPairingInfo] = useState<PairingInfo | undefined>(undefined);

  /**
   * Use this to update the pairingInfo when pairing
   */
  const getPairingInfo = async () => {
    props.deviceId &&
      getPairingInfoByDeviceId(props.deviceId).then((result) => {
        if (result.status === 200) {
          setPairingInfo(result.data);
          setHistoryNeedsUpdate(false);
        }
      });

    props.equipmentId &&
      getPairingInfoByEquipmentId(props.equipmentId).then((result) => {
        if (result.status === 200) {
          setPairingInfo(result.data);
          setHistoryNeedsUpdate(false);
        }
      });
  };

  useEffect(() => {
    getPairingInfo();
  }, [props.deviceId, props.equipmentId]);

  const pair = async (deviceId, equipmentId) => {
    if (!pairingInfo) return;

    const imei = 'backoffice';
    const now = new Date();
    const pairingRequest: PairingRequest = {
      imei: imei,
      paired: 1,
      unPaired: 0,
      deviceFailures: 0,
      equipmentFailures: 0,
      uniqueDevicesPaired: 0,
      uniqueEquipmentPaired: 0,
      at: {
        type: 'Point',
        longitude: 0,
        latitude: 0,
        dateTime: now,
      },
      equipmentPairingRequest: {
        equipmentId: equipmentId,
        deviceId: deviceId,
        pairingMethod: 5, // 5 == Backoffice
      },
    } as PairingRequest;
    // @todo refactor, it is used twice in the same way and will be used two times more when pairing from equipment details

    await pairDevice(undefined, pairingRequest)
      .then(async (res) => {
        if (res.status === 200) {
          notifyMessage('Paired!', 'success');

          // get the new information
          await getPairingInfo();

          setHistoryNeedsUpdate(true);
        } else {
          // @todo use the error message at the root level of the response if possible
          notifyMessage('Failed to pair', 'error');
        }
      })
      .catch(() => {
        notifyMessage('Unable to pair device', 'error');
      });
  };

  const unpair = async (values) => {
    if (!pairingInfo) return;

    const imei = 'backoffice';
    const now = new Date();
    const unpairRequest: UnpairRequest = {
      at: {
        type: 'Point',
        longitude: 0,
        latitude: 0,
        dateTime: now,
      },
      imei: imei,
      paired: 0,
      unPaired: 1,
      deviceFailures: 0,
      equipmentFailures: 0,
      uniqueDevicesPaired: 0,
      uniqueEquipmentPaired: 0,
      ...values,
    } as UnpairRequest;

    await unpairDevice(undefined, pairingInfo.device.deviceId, unpairRequest)
      .then(async () => {
        notifyMessage('Unpaired!', 'success');

        // get the new information
        await getPairingInfo();

        setHistoryNeedsUpdate(true);
      })
      .catch(() => {
        notifyMessage('Unable to unpair device', 'error');
      });
  };

  return (
    <PairingInfoDetails
      historyNeedsUpdate={historyNeedsUpdate}
      pairingInfo={pairingInfo}
      noHistoryCardRadius={props.noHistoryCardRadius}
      isFromDevicePerspective={props.deviceId !== undefined}
      pair={pair}
      unpair={unpair}
    />
  );
}
